<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="smk-details-main">
    <div class="content-container">
      <div class="header">
        <div class="title"> Summary Details for Job {{ jobDetails['Job No'] || jobDetails['SMK Job No'] || '' }} </div>
      </div>
      <div class="body-container" :class="{ 'is-loading': isLoading }">
        <div class="loading-overlay" v-if="isLoading">
          <div class="loading-spinner"></div>
        </div>
        <BoxContainer
          :isCollapsible="true"
          :isCollapsed.sync="headerBoxContainersCollapsed.submissionInfo"
          title="SMK Submission Status"
          ref="submissionInfo"
          @collapseArrowClicked="boxContainerCollapseArrowClicked"
        >
          <div class="table-wrapper" v-if="smkStatus.length > 0">
            <div v-if="!headerBoxContainersCollapsed.submissionInfo" class="table-toolbar">
              <button class="smk-item-expand-btn" @click="showSmkSubmissionStatusPopup = true">
                <img :src="'https://dsmlib.logistics.systems/Icons/Expand.png'" class="smk-item-expand-icon" />
              </button>
            </div>
            <table class="table">
              <thead>
                <tr class="table-title">
                  <th v-for="col in smkStatusColumns" :key="col.key">
                    {{ col.label }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(s, index) in smkStatus" :key="index">
                  <td v-for="col in smkStatusColumns" :key="col.key">
                    <span
                      v-if="col.key === 'Request Type'"
                      class="request-type-pill"
                      :style="{ backgroundColor: s.BackColor }"
                    >
                      {{ s[col.key] }}
                    </span>

                    <span v-else>
                      {{ s[col.key] }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </BoxContainer>
        <PopupV2
          :show="showSmkSubmissionStatusPopup"
          type="info"
          title="SMK Submission Status - Full Table View"
          height="90%"
          width="90%"
          :buttons="smkSubmissionStatusPopupButtons"
          @buttonClicked="smkSubmissionStatusPopupButtonsClicked"
        >
          <div class="table-popup">
            <div class="popup-table-wrapper">
              <table class="table">
                <thead>
                  <tr class="table-title">
                    <th v-for="col in smkStatusColumns" :key="col.key">
                      {{ col.label }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(s, index) in smkStatus" :key="index">
                    <td v-for="col in smkStatusColumns" :key="col.key">
                      <span
                        v-if="col.key === 'Request Type'"
                        class="request-type-pill"
                        :style="{ backgroundColor: s.BackColor }"
                      >
                        {{ s[col.key] }}
                      </span>

                      <span v-else>
                        {{ s[col.key] }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </PopupV2>
        <!-- Job Header -->
        <BoxContainer
          :isCollapsible="true"
          :isCollapsed.sync="headerBoxContainersCollapsed.headerInfo"
          title="Job Header"
          ref="headerInfo"
          @collapseArrowClicked="boxContainerCollapseArrowClicked"
        >
          <div class="details-table-container">
            <table class="details-table">
              <tr>
                <td class="label">Date</td>
                <td class="colon">:</td>
                <td class="details" colspan="5"
                  ><b>{{ selectedJob['Job DateTime'] || '' }}</b></td
                >

                <td class="spacer" />

                <td class="label">Project #</td>
                <td class="colon">:</td>
                <td class="details"
                  ><b>{{ jobDetails['Project No'] || '' }}</b></td
                >
              </tr>
              <tr>
                <td class="label">
                  {{ selectedJob['Job Type'] === 'IMP' ? 'Consignee' : 'Shipper' }}
                </td>
                <td class="colon">:</td>
                <td class="details" colspan="5">
                  <b>
                    {{
                      selectedJob['Job Type'] === 'IMP' ?
                        jobDetails['Consignee Name'] || ''
                      : jobDetails['Shipper Name'] || ''
                    }}
                  </b>
                </td>

                <td class="spacer" />

                <td class="label">Custom Form</td>
                <td class="colon">:</td>
                <td class="details"
                  ><b>{{ selectedJob['SMK Form Type'] || '' }}</b></td
                >
              </tr>
              <tr>
                <td class="label">{{ pageType == 'Import' ? 'Import Type' : 'Export Type' }}</td>
                <td class="colon">:</td>
                <td class="details" colspan="5"
                  ><b>{{ selectedJob['Cargo Type'] || '' }}</b></td
                >

                <td class="spacer" />

                <td class="label">Booking Ref #</td>
                <td class="colon">:</td>
                <td class="details"
                  ><b>{{ jobDetails['Booking Ref No'] || '' }}</b></td
                >
              </tr>
            </table>
          </div>
        </BoxContainer>
        <!-- SMK Job Detail -->
        <BoxContainer
          :isCollapsible="true"
          :isCollapsed.sync="headerBoxContainersCollapsed.smkInfo"
          title="SMK Job Detail"
          ref="smkInfo"
          @collapseArrowClicked="boxContainerCollapseArrowClicked"
        >
          <div class="details-table-container">
            <table class="details-table">
              <tr v-for="row in smkJobDetailRows" :key="row.label">
                <td class="label" style="width: 15%">
                  {{ row.label }}
                </td>

                <td class="colon" style="width: 1%"> : </td>

                <td class="details" colspan="5">
                  <b>{{ row.value || '' }}</b>
                </td>
              </tr>
            </table>
          </div>
        </BoxContainer>
        <div class="details-card">
          <div class="header" :style="detailsCardHeaderStyle">
            <div class="label"> Job Details </div>
            <img
              class="expand-all"
              src="https://dsmlib.logistics.systems/Icons/Expand.png"
              alt="Expand All"
              title="Expand All"
              @click="expandJobBoxContainers"
            />
            <img
              class="collapse-all"
              src="https://dsmlib.logistics.systems/Icons/Collapse.png"
              alt="Collapse All"
              title="Collapse All"
              @click="collapseJobBoxContainers"
            />
          </div>
          <div class="details-container">
            <div class="details">
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="detailsBoxContainersCollapsed.shippingInfo"
                title="🚢 Shipping Info"
                @collapseArrowClicked="boxContainerCollapseArrowClicked"
              >
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label">Ship Call No</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Ship Call No'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Commodity</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Commodity Code'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Voy No</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Voy No'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Final Dest</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Final Dest'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Vessel ID</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Vessel ID'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Payment City</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Pay Country'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Vessel Name</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Vessel Name'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Station Code</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Station Code'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Shipping Agent</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Shipping Agt Name'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Port Operator</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Port Operator'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Shipping Line</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Shipping Line Name'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Closing Time</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Closing Time'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Port Of Loading</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Port of Loading'] || '' }} - {{ jobDetails['POL Name'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">{{ pageType == 'Import' ? 'POL ETD' : 'POL ETA' }}</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ pageType == 'Import' ? jobDetails['POL ETD'] : jobDetails['POL ETA'] }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Port Of Discharge</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Port of Discharge'] || '' }} - {{ jobDetails['POD Name'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">POD ETA</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['POD ETA'] || '' }}</b></td
                      >
                    </tr>
                  </table>
                </div>
              </BoxContainer>
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="detailsBoxContainersCollapsed.cargoInfo"
                title="👤 Importer Exporter Info"
                @collapseArrowClicked="boxContainerCollapseArrowClicked"
              >
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label" style="width: 9%">Shipper</td>
                      <td class="colon" style="width: 0%">:</td>
                      <td class="details" colspan="5"
                        ><b>{{ jobDetails['Shipper'] || '' }} - {{ jobDetails['Shipper Name'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label" style="width: 9%">Consignee</td>
                      <td class="colon" style="width: 0%">:</td>
                      <td class="details" colspan="5"
                        ><b>{{ jobDetails['Consignee'] || '' }} - {{ jobDetails['Consignee Name'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label" style="width: 9%">Notify Party</td>
                      <td class="colon" style="width: 0%">:</td>
                      <td class="details" colspan="5"
                        ><b>{{ jobDetails['Notify Party'] || '' }} - {{ jobDetails['Notify Party Name'] || '' }}</b></td
                      >
                    </tr>
                  </table>
                </div>
              </BoxContainer>
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="detailsBoxContainersCollapsed.importerExporterInfo"
                title="📦 Cargo Info"
                @collapseArrowClicked="boxContainerCollapseArrowClicked"
              >
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label">Gross Weight</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Gross Weight'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Measurement</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Volume'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">No Of Packages</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Total Packages'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Type Of Packages</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Package Code'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Packing Material</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ jobDetails['Packing Material'] || '' }}</b></td
                      >
                    </tr>
                  </table>
                </div>
              </BoxContainer>
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="detailsBoxContainersCollapsed.importerExporterInfo"
                title="🚚 Container Details"
                @collapseArrowClicked="boxContainerCollapseArrowClicked"
              >
                <div class="details-table-container">
                  <div class="table-wrapper" v-if="jobDetails.ContDetail && jobDetails.ContDetail.length > 0">
                    <table class="table">
                      <thead>
                        <tr class="table-title">
                          <th>#</th>
                          <th>Container #</th>
                          <th>SMK Job #</th>
                          <th>Size</th>
                          <th>Type</th>
                          <th>DelType</th>
                          <th>HdgType</th>
                          <th>Seal #</th>
                          <th>Cont Status</th>
                          <th>Remarks</th>
                          <th>ContId</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr v-for="(cont, index) in jobDetails.ContDetail" :key="index">
                          <td>{{ index + 1 }}</td>
                          <td>{{ cont['Container No'] }}</td>
                          <td>{{ cont['SMK Job No'] }}</td>
                          <td>{{ cont['Size'] }}</td>
                          <td>{{ cont['Cont Type'] }}</td>
                          <td>{{ cont['Delivery Type'] }}</td>
                          <td>{{ cont['Handling Type'] }}</td>
                          <td>{{ cont['Seal No'] }}</td>
                          <td>{{ cont['Cont Status Value'] }}</td>
                          <td>{{ cont['Remarks'] }}</td>
                          <td>{{ cont['ContId'] }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </BoxContainer>
            </div>
          </div>
        </div>
        <div class="details-card">
          <div class="header" :style="detailsCardHeaderStyle">
            <div class="label"> Exemption </div>
            <img
              class="expand-all"
              src="https://dsmlib.logistics.systems/Icons/Expand.png"
              alt="Expand All"
              title="Expand All"
              @click="expandExemptionBoxContainers"
            />
            <img
              class="collapse-all"
              src="https://dsmlib.logistics.systems/Icons/Collapse.png"
              alt="Collapse All"
              title="Collapse All"
              @click="collapseExemptionBoxContainers"
            />
          </div>
          <div class="details-container">
            <div class="details">
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="exemptionBoxContainersCollapsed.treasuryInfo"
                title="Treasury Info"
                @collapseArrowClicked="boxContainerCollapseArrowClicked"
              >
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label" style="width: 9%">Ref No</td>
                      <td class="colon" style="width: 0%">:</td>
                      <td class="details" colspan="5"
                        ><b>{{ smkExempt['TERefNo'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Exempt Type</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ smkExempt['TEType'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Expiry Date</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ smkExempt['TEExpiryDate'] || '' }}</b></td
                      >
                    </tr>
                  </table>
                </div>
              </BoxContainer>
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="exemptionBoxContainersCollapsed.salesTaxInfo"
                title="Sales Tax Info"
                @collapseArrowClicked="boxContainerCollapseArrowClicked"
              >
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label">Ref No</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ smkExempt['STRefNo'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Exempt Type</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ smkExempt['STType'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Expiry Date</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ smkExempt['STExpiryDate'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Registration Date</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ smkExempt['STRegDate'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label">Station Code</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ smkExempt['STStationCode'] || '' }}</b></td
                      >
                    </tr>
                  </table>
                </div>
              </BoxContainer>
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="exemptionBoxContainersCollapsed.specialInfo"
                title="Special Treatment"
                @collapseArrowClicked="boxContainerCollapseArrowClicked"
              >
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label">Ref No</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ smkExempt['APRefNo'] || '' }}</b></td
                      >

                      <td class="spacer" />

                      <td class="label">Exempt Type</td>
                      <td class="colon">:</td>
                      <td class="details"
                        ><b>{{ smkExempt['APType'] || '' }}</b></td
                      >
                    </tr>
                  </table>
                </div>
              </BoxContainer>
              <BoxContainer
                :isCollapsible="true"
                :isCollapsed.sync="exemptionBoxContainersCollapsed.claimantInfo"
                title="Claimant Details"
                @collapseArrowClicked="boxContainerCollapseArrowClicked"
              >
                <div class="details-table-container">
                  <table class="details-table">
                    <tr>
                      <td class="label" style="width: 9%">Name</td>
                      <td class="colon" style="width: 0%">:</td>
                      <td class="details" colspan="5"
                        ><b>{{ smkExempt['CMName'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label" style="width: 9%">Company</td>
                      <td class="colon" style="width: 0%">:</td>
                      <td class="details" colspan="5"
                        ><b>{{ smkExempt['CMCompany'] || '' }}</b></td
                      >
                    </tr>
                    <tr>
                      <td class="label" style="width: 9%">Status / NRIC</td>
                      <td class="colon" style="width: 0%">:</td>
                      <td class="details" colspan="5"
                        ><b>{{ smkExempt['CMStatus'] || '' }} - {{ smkExempt['CMNRIC'] || '' }}</b></td
                      >
                    </tr>
                  </table>
                </div>
              </BoxContainer>
            </div>
          </div>
        </div>
        <BoxContainer style="margin-top: 10px" :isCollapsible="true" title="📁 Documents">
          <div class="details-table-container">
            <div class="table-wrapper" v-if="smkDoc && smkDoc.length > 0">
              <table class="table">
                <thead>
                  <tr class="table-title">
                    <th>#</th>
                    <th>Doc</th>
                    <th>SmkDoc</th>
                    <th>SMK Ref #</th>
                    <th>Issue Date</th>
                    <th>Expiry Date</th>
                    <th>Invoice Date</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(doc, index) in smkDoc" :key="index">
                    <td>{{ index + 1 }}</td>
                    <td>{{ doc['Doc'] }}</td>
                    <td>{{ doc['SmkDoc'] }}</td>
                    <td>{{ doc['SMK Ref #'] }}</td>
                    <td>{{ doc['IssueDate'] }}</td>
                    <td>{{ doc['ExpiryDate'] }}</td>
                    <td>{{ doc['InvoiceDate'] }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </BoxContainer>
        <BoxContainer
          style="margin-top: 10px"
          :isCollapsible="true"
          title="📝 SMK Items"
          ref="smkItemsInfo"
          @collapseArrowClicked="boxContainerCollapseArrowClicked"
        >
          <div class="details-table-container">
            <div class="table-wrapper" v-if="smkItems && smkItems.length > 0" @click="showItemTablePopup = true">
              <div class="table-toolbar">
                <button class="smk-item-expand-btn" @click="showItemTablePopup = true">
                  <img :src="'https://dsmlib.logistics.systems/Icons/Expand.png'" class="smk-item-expand-icon" />
                </button>
              </div>
              <table class="table smk-items-grouped-table">
                <thead>
                  <tr class="table-title">
                    <th v-for="col in visibleSMKItemColumns" :key="col.key" :class="smkItemColumnGroupClasses(col)">
                      {{ col.label }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(item, index) in smkItems" :key="item.TxnId || index">
                    <td v-for="col in visibleSMKItemColumns" :key="col.key" :class="smkItemColumnGroupClasses(col)">
                      {{ smkItemCellValue(item, col, index) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </BoxContainer>
        <PopupV2
          :show="showItemTablePopup"
          type="info"
          title="SMK Items - Full Table View"
          height="90%"
          width="90%"
          :buttons="showItemTablePopupButtons"
          @buttonClicked="showItemTablePopupButtonsClicked"
        >
          <div class="table-popup">
            <div
              v-if="selectedSMKItemRowForDuty && showSMKItemHoverTables"
              class="smk-item-duty-panel"
              :class="{ 'with-cess': showSMKCessTable }"
              :style="{
                left: hoverSMKItemPanelX + 'px',
                top: hoverSMKItemPanelY + 'px',
              }"
            >
              <div class="smk-item-hover-tables">
                <table class="smk-item-duty-table">
                  <thead>
                    <tr>
                      <th rowspan="2"></th>
                      <th colspan="3">Duty</th>
                      <th colspan="3">Exemption</th>
                    </tr>
                    <tr>
                      <th>%</th>
                      <th>$</th>
                      <th>Amount</th>
                      <th>%</th>
                      <th>$</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in smkItemDutyRows" :key="row.label">
                      <td>{{ row.label }}</td>
                      <td>{{ row.dutyPercent }}</td>
                      <td>{{ row.dutyFixed }}</td>
                      <td>{{ row.dutyAmount }}</td>
                      <td>{{ row.exemptPercent }}</td>
                      <td>{{ row.exemptFixed }}</td>
                      <td>{{ row.exemptAmount }}</td>
                    </tr>
                  </tbody>
                </table>

                <table v-if="showSMKCessTable" class="smk-item-cess-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Cess Rate</th>
                      <th>CessAmt</th>
                      <th>Xm%</th>
                      <th>Cess XM</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in smkItemCessRows" :key="row.label">
                      <td>{{ row.label }}</td>
                      <td>{{ row.cessRate }}</td>
                      <td>{{ row.cessAmount }}</td>
                      <td>{{ row.exemptPercent }}</td>
                      <td>{{ row.exemptAmount }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="smk-sidebar-column-toggles">
              <button
                type="button"
                class="smk-column-toggle-button"
                :class="{ active: showSMKItemHoverTables }"
                title="Show/Hide Duty Table"
                @click.stop="$emit('update:showSMKItemHoverTables', !showSMKItemHoverTables)"
              >
                <img src="https://dsmlib.logistics.systems/Icons/table.png" alt="Duty Table" />
              </button>

              <button
                type="button"
                class="smk-column-toggle-button"
                :class="{ active: showSMKVehicleColumns }"
                title="Show/Hide Vehicle Columns"
                @click.stop="$emit('update:showSMKVehicleColumns', !showSMKVehicleColumns)"
              >
                <img src="https://dsmlib.logistics.systems/Icons/car.png" alt="Vehicle" />
              </button>

              <button
                type="button"
                class="smk-column-toggle-button"
                :class="{ active: showSMKWarehouseColumns }"
                title="Show/Hide Warehouse Columns"
                @click.stop="$emit('update:showSMKWarehouseColumns', !showSMKWarehouseColumns)"
              >
                <img src="https://dsmlib.logistics.systems/Icons/smk-warehouse.png" alt="Warehouse" />
              </button>
            </div>
            <div class="popup-table-wrapper">
              <table class="table smk-items-grouped-table">
                <thead>
                  <tr class="table-title">
                    <th v-for="col in visibleSMKItemColumns" :key="col.key" :class="smkItemColumnGroupClasses(col)">
                      {{ col.label }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="(item, index) in smkItems"
                    :key="item.TxnId || index"
                    @mouseenter="showSMKItemDutyPanel(item, $event)"
                    @mousemove="moveSMKItemDutyPanel($event)"
                    @mouseleave="hideSMKItemDutyPanel(item)"
                  >
                    <td v-for="col in visibleSMKItemColumns" :key="col.key" :class="smkItemColumnGroupClasses(col)">
                      {{ smkItemCellValue(item, col, index) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </PopupV2>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {},
  computed: {
    SendToAdrress: function () {
      if (!this.details || this.details.length === 0) {
        return '';
      }

      const detail = this.jobDetails;
      const address = [];

      // function to bold text
      const bold = (value) => `<b>${value}</b>`;

      if (detail.SendToCompany) address.push(bold(detail.SendToCompany));
      if (detail.SendToAddr1) address.push(bold(detail.SendToAddr1));
      if (detail.SendToAddr2) address.push(bold(detail.SendToAddr2));
      if (detail.SendToAddr3) address.push(bold(detail.SendToAddr3));

      const addr1 = [];
      if (detail.SendToPostCode) addr1.push(detail.SendToPostCode);
      if (detail.SendToCity) addr1.push(detail.SendToCity);

      if (addr1.length > 0) {
        address.push(bold(addr1.join(', ')));
      }

      const addr2 = [];
      if (detail.SendToState) addr2.push(bold(detail.SendToState));
      if (detail.SendToCountry) addr2.push(bold(detail.SendToCountry));

      if (addr2.length > 0) {
        address.push(bold(addr2.join(', ')));
      }

      return address.join('<br>');
    },
    detailsCardHeaderStyle: function () {
      if (
        !this.cnDetailsCardHeaderBackgroundColor ||
        this.cnDetailsCardHeaderBackgroundColor === 'white' ||
        !this.cnDetailsCardHeaderBackgroundColor.startsWith('#')
      ) {
        return {
          'background-color': 'white',
          color: 'black',
        };
      }

      let backgroundColor = this.cnDetailsCardHeaderBackgroundColor.replace('#', '');
      if (backgroundColor.length == 3) {
        // if there are only 3 hex digits
        backgroundColor =
          backgroundColor[0] +
          backgroundColor[0] +
          backgroundColor[1] +
          backgroundColor[1] +
          backgroundColor[2] +
          backgroundColor[2];
      }

      const red = parseInt(backgroundColor.slice(0, 2), 16); // convert from hex to decimal
      const green = parseInt(backgroundColor.slice(2, 4), 16);
      const blue = parseInt(backgroundColor.slice(4, 6), 16);

      let textColor;
      if ((red + green + blue) / 3 > 100) {
        textColor = 'black';
      } else {
        textColor = 'white';
      }

      return {
        'background-color': this.cnDetailsCardHeaderBackgroundColor,
        color: textColor,
      };
    },
    premisesStatus: function () {
      return this.details?.[0]?.PremisesIs || '';
    },
    premisesColor: function () {
      return this.premisesStatus.toUpperCase() === 'UNSECURED' ? 'red' : '';
    },
    selectedSMKItemRowForDuty: function () {
      if (this.hoverSMKItemRow && (this.smkItems || []).includes(this.hoverSMKItemRow)) {
        return this.hoverSMKItemRow;
      }

      return null;
    },
    smkItemDutyRows: function () {
      if (!this.selectedSMKItemRowForDuty || !this.smkItemDutyRowsGetter) return [];
      return this.smkItemDutyRowsGetter(this.selectedSMKItemRowForDuty);
    },
    visibleSMKItemColumns: function () {
      return (this.smkItemColumns || []).filter((col) => {
        return !['select', 'action'].includes(col.key);
      });
    },
    smkItemCessRows: function () {
      if (!this.selectedSMKItemRowForDuty || !this.smkItemCessRowsGetter) return [];

      return this.smkItemCessRowsGetter(this.selectedSMKItemRowForDuty);
    },
  },
  data: function () {
    return {
      cnDetailsCardHeaderBackgroundColor: 'white',
      headerBoxContainersCollapsed: {
        headerInfo: false,
        smkInfo: false,
        submissionInfo: false,
      },
      detailsBoxContainersCollapsed: {
        shippingInfo: false,
        importerExporterInfo: false,
        cargoInfo: false,
      },
      exemptionBoxContainersCollapsed: {
        treasuryInfo: false,
        salesTaxInfo: false,
        specialInfo: false,
        claimantInfo: false,
      },
      showSmkSubmissionStatusPopup: false,
      showItemTablePopup: false,
      hoverSMKItemRow: null,
      hoverSMKItemPanelX: 0,
      hoverSMKItemPanelY: 0,
      smkStatusColumns: [
        { label: 'Request Type', key: 'Request Type' },
        { label: 'Code', key: 'Status Code' },
        { label: 'Remarks', key: 'Remarks' },
        { label: 'SMK #', key: 'SMK #' },
        { label: 'Duty Type', key: 'Duty Type' },
        { label: 'Amount', key: 'Amount' },
        { label: 'Submit Date Time', key: 'Submit Date Time' },
        { label: 'DateUpdate', key: 'DateUpdate' },
        { label: 'SNRF', key: 'SNRF' },
        { label: 'Ref No', key: 'Ref No' },
      ],
      showItemTablePopupButtons: [{ text: 'Delete All Items', color: 'red', textColor: 'white' }, { text: 'Close' }],
      smkSubmissionStatusPopupButtons: [{ text: 'Close' }],
    };
  },
  props: {
    jobDetails: {
      type: Object,
      default: function () {
        return {};
      },
    },
    selectedJob: {
      type: Object,
      default: function () {
        return {};
      },
    },
    smkDetails: {
      type: Object,
      default: function () {
        return {};
      },
    },
    smkDoc: {
      type: Array,
      default: function () {
        return [];
      },
    },
    smkItems: {
      type: Array,
      default: function () {
        return [];
      },
    },
    smkItemColumns: {
      type: Array,
      default: function () {
        return [];
      },
    },
    smkExempt: {
      type: Object,
      default: function () {
        return {};
      },
    },
    attachmentList: {
      type: Array,
      default: function () {
        return [];
      },
    },
    smkStatus: {
      type: Array,
      default: function () {
        return [];
      },
    },
    smkItemDutyRowsGetter: {
      type: Function,
      default: null,
    },
    showSMKItemHoverTables: {
      type: Boolean,
      default: false,
    },
    showSMKVehicleColumns: {
      type: Boolean,
      default: false,
    },
    showSMKWarehouseColumns: {
      type: Boolean,
      default: false,
    },
    smkItemCessRowsGetter: {
      type: Function,
      default: null,
    },
    showSMKCessTable: {
      type: Boolean,
      default: false,
    },
    smkJobDetailRows: {
      type: Array,
      default: function () {
        return [];
      },
    },
    pageType: { type: String, default: '' },
    // expected format for details:
    // obobtainable from API endpoint: Service6G.svc/GetCNDetails
    // [
    //   {
    //     PUArea: "WP",
    //     DOArea: "WP",
    //     PickUp: "KMT",
    //     DropOff: "GFORTUNE",
    //     ConsignmentNo: "LI0137797-001",
    //     RequestNo: "B25005882",
    //     ContainerNo: "EMCU1611193",
    //     ContSizeType: "40HC",
    //     HandlingType: "N",
    //     DeliveryType: "TR",
    //     TotalCount: 3,
    //     CurrentStatus: "Pending Delivery",
    //     Operator: "EMC",
    //     Commodity: "WHEEL DISC",
    //     Trip: "1         ",
    //     PremisesIs: "UNSECURED",
    //     SendToCode: "WZ12",
    //     SendToArea: "BTGKL",
    //     SendToCompany: "WAREHOUSE ZURAH",
    //     SendToAddr1: "LOT PT803, JALAN ZURAH 7",
    //     SendToAddr2: "KAWASAN PERINDUSTRIAN ZURAH",
    //     SendToAddr3: "",
    //     SendToPostCode: "44200",
    //     SendToCity: "BATANG KALI",
    //     SendToState: "",
    //     SendToCountry: "",
    //     ContactPerson: "NISA",
    //     ContactEmail: "-",
    //     Phone1: "0102361980",
    //     Phone2: "-",
    //     Fax1: "-",
    //     Fax2: "-",
    //     RequiredDate: "2025-05-31 10:00",
    //     RequestDate: "2025-05-30 15:09",
    //     CloseDate: "-",
    //     ETADate: "2025-05-28 08:00",
    //     ETDDate: "-",
    //     DateDemurrage: "-",
    //     DateDetention: "-",
    //     PlannedDate: "-",
    //     SCN: "254SOE",
    //     VesselNo: "FIT694199",
    //     VesselName: "ITAL BONUS",
    //     ShipLine: "BS0557",
    //     ForwardingAgent: "TLS01",
    //     ShippingAgent: "BS0218",
    //     DischargePort: "JAKARTA",
    //     DocNo: "-",
    //     DocTitle: "Gate Pass",
    //     SealNo: "EMCPKW9374",
    //     OfficeHrs: 1,
    //     LastUserID: "HAZRIE01            ",
    //     LastDateUpdated: "2025-05-30 15:09",
    //     JobType: "IMP",
    //     BillTo: "3000TL01",
    //     Fumigation: "N",
    //     CNLastUpStatus: "8001 ",
    //     JobRemarks: null,
    //     VoyNo: "0453-103N",
    //     BL_No: "",
    //     CustRef: "P2I02350525",
    //     ShprCsgnName: "PERODUA GLOBAL MANUFACTURING SDN BHD",
    //     ShprCsgnCode: "TL01PG01",
    //     BookingRefNo: "HASLS16250800605"
    //   },
    // ];
    isLoading: { type: Boolean, default: false }, // loading state prop
  },
  methods: {
    expandJobBoxContainers: function () {
      if (this.isLoading) return;
      Object.keys(this.detailsBoxContainersCollapsed).forEach((key) => {
        this.detailsBoxContainersCollapsed[key] = false;
      });
    },
    collapseJobBoxContainers: function () {
      if (this.isLoading) return;
      Object.keys(this.detailsBoxContainersCollapsed).forEach((key) => {
        this.detailsBoxContainersCollapsed[key] = true;
      });
    },
    expandExemptionBoxContainers: function () {
      if (this.isLoading) return;
      Object.keys(this.exemptionBoxContainersCollapsed).forEach((key) => {
        this.exemptionBoxContainersCollapsed[key] = false;
      });
    },
    collapseExemptionBoxContainers: function () {
      if (this.isLoading) return;
      Object.keys(this.exemptionBoxContainersCollapsed).forEach((key) => {
        this.exemptionBoxContainersCollapsed[key] = true;
      });
    },
    boxContainerCollapseArrowClicked: function (collapsed, refName) {
      if (this.isLoading) return;
      if (!collapsed) {
        this.$nextTick(() => {
          if (this.$refs[refName]) {
            this.$refs[refName].updateExpandedHeight();
          }
        });
      }
    },
    reupdateExpandedHeight: function () {
      Object.keys(this.$refs).forEach((ref) => {
        if (this.$refs[ref] && this.$refs[ref].updateExpandedHeight) {
          this.$refs[ref].updateExpandedHeight();
        }
      });
    },
    smkSubmissionStatusPopupButtonsClicked: async function (e) {
      if (e == 'Close') {
        this.showSmkSubmissionStatusPopup = false;
        return;
      }
    },
    showItemTablePopupButtonsClicked: async function (e) {
      if (e == 'Close') {
        this.showItemTablePopup = false;
        this.hoverSMKItemRow = null;
        return;
      }
      if (e == 'Delete All Items') {
        if (!this.smkItems || this.smkItems.length === 0) return;
        this.$emit('deleteSMKItemAllSideBar');
      }
    },
    showSMKItemDutyPanel: function (row, event) {
      if (!row) return;

      this.hoverSMKItemRow = row;
      this.moveSMKItemDutyPanel(event);
    },

    moveSMKItemDutyPanel: function (event) {
      if (!event) return;

      const panelWidth = this.showSMKCessTable ? 710 : 430;
      const panelHeight = 175;
      const gap = 4;
      const maxLeft = Math.max(4, window.innerWidth - panelWidth - 4);
      const maxTop = Math.max(4, window.innerHeight - panelHeight - 4);

      this.hoverSMKItemPanelX = Math.min(event.clientX + gap, maxLeft);
      this.hoverSMKItemPanelY = Math.min(event.clientY + gap, maxTop);
    },

    hideSMKItemDutyPanel: function (row) {
      if (this.hoverSMKItemRow === row) {
        this.hoverSMKItemRow = null;
      }
    },
    smkItemCellValue: function (item, col, index) {
      if (!item || !col) return '';
      if (col.key === 'seq') return index + 1;

      const value = item[col.key];
      if (value === null || value === undefined || value === '') return '';

      if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return '';
        return value.toLocaleDateString('en-GB').replace(/\//g, '-');
      }

      if (typeof value === 'object') {
        const keys = [
          col.objectKey,
          'Item Code',
          'Product Code',
          'Country Code',
          'Tariff Code',
          'UOM Code',
          'Alcohol',
          'Code',
          'Name',
        ].filter(Boolean);

        for (const key of keys) {
          if (value[key] !== null && value[key] !== undefined) {
            return value[key];
          }
        }

        return '';
      }

      return value;
    },
    smkItemColumnGroupClasses: function (col) {
      if (!col || !col.group) return [];

      return [
        'smk-field-group',
        `smk-group-${col.group}`,
        {
          'smk-group-start': col.groupStart,
          'smk-group-end': col.groupEnd,
        },
      ];
    },
  },
  async mounted() {
    this.$bus.$on('backColorChanged', (backgroundColor) => {
      // getting backgroundColor from user options is asynch, therefore need to listen to an event
      this.cnDetailsCardHeaderBackgroundColor = backgroundColor; // and change the background color of the header once it has finished loading
    });
    this.cnDetailsCardHeaderBackgroundColor = this.$store.state['backgroundColor']; // initialise background color to whatever is in vuex at the time of component mount
  },
  beforeDestroy() {
    this.$bus.$off('backColorChanged'); // stop listening to the event
  },
  watch: {
    details: {
      deep: true,
      handler: function () {
        this.$nextTick(() => {
          this.reupdateExpandedHeight();
        });
      },
    },
    smkItems: {
      deep: true,
      handler: function () {
        this.$nextTick(() => {
          this.reupdateExpandedHeight();
        });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.smk-details-main {
  > .content-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;

    > .header {
      margin-bottom: 5px;
      border-radius: 5px 5px 0 0;
      height: 30px;
      width: 100%;
      background-color: #535cda;
      padding-left: 5px;
      display: flex;
      align-items: center;

      > .title {
        color: white;
        font-weight: bold;
      }
    }

    > .body-container {
      position: relative;
      height: calc(100% - 30px);
      width: 100%;
      overflow: hidden;
      transition: opacity 0.3s ease;

      &.is-loading {
        opacity: 0.4;
        pointer-events: none;
      }

      .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        background-color: rgba(255, 255, 255, 0.1);

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 5px solid #f3f3f3;
          border-top: 5px solid #535cda;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      }
    }
  }
}

.details-card > .header {
  height: 40px;
  padding: 0 15px;
  background: linear-gradient(to right, #3b82f6, #60a5fa);
  border-bottom: 1px solid #2563eb;
}

.details-card > .header > .label {
  font-size: 14px;
  letter-spacing: 0.3px;
  color: #ffffff;
  font-weight: bold;
}

.details-table tr:nth-child(even) {
  background: #fafafa;
}

.details-table tr:hover {
  background: #f3f6ff;
}

.details-card {
  margin: 10px 0px 10px 0px;
  border: 1px solid black;
  border-radius: 5px;
  height: fit-content;
  width: 100%;

  > .header {
    border-bottom: 1px solid black;
    border-radius: 5px 5px 0 0;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    > .label {
      font-weight: bold;
    }

    > .expand-all,
    > .collapse-all {
      position: absolute;
      height: 20px;
      width: 20px;
      transition: 0.3s;
      cursor: pointer;
      filter: brightness(0) invert(1);

      &:hover {
        height: 40px;
        width: 40px;
      }
    }

    > .expand-all {
      right: 45px;

      &:hover {
        right: 35px;
      }
    }

    > .collapse-all {
      right: 15px;

      &:hover {
        right: 5px;
      }
    }
  }

  > .details-container {
    padding: 5px;
    display: flex;
    flex-direction: column;
  }

  > .details {
    display: flex;
    flex-direction: column;
  }
}

.details-table-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .details-table {
    width: 100%;
  }
  .details-table .label {
    width: 26%;
  }
  .details-table .colon {
    width: 1%;
  }
  .details-table .details {
    width: 27%;
  }
  .details-table .spacer {
    width: 0.5%;
  }
}

.table-wrapper {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 300px;
}

.smk-items-summary-table {
  cursor: pointer;
}

.table-title th {
  color: white !important;
}

.table {
  width: max-content;
  min-width: 100%;
  table-layout: auto;
  white-space: nowrap;

  border-collapse: collapse;
  border: 1px solid #000;
}

.table th {
  background: #f8fafc;
  color: #374151;
  text-align: left;
  padding: 10px 12px;
  white-space: nowrap;
  background-color: #535cda !important;
  border: 1px solid #e5e7eb;
}

.table td {
  padding: 10px 12px;
  white-space: nowrap;
  border: 1px solid #d1d5db;
}

.table tbody tr:nth-child(odd) {
  background: #e2e4ff;
}

.table tbody tr:hover {
  background-color: #a6acff;
}

.table-popup {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.popup-table-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  border: 1px solid black;
}

.table-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
}

.smk-item-expand-btn {
  z-index: 1;
  position: absolute;
  width: 22px;
  height: 22px;
  padding: 2px;
  margin-right: 6px;
  border: 1px solid #7d8be8;
  background: #eef1ff;
  border-radius: 3px;
  cursor: pointer;
  line-height: 1;

  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
}

.smk-item-expand-btn:hover {
  background: #dfe5ff;
}

.smk-item-expand-icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
  display: block;
  transition: 0.3s;

  &:hover {
    height: 20px;
    width: 20px;
  }
}

.details-table .label,
.details-table .colon,
.details-table .details {
  vertical-align: top;
}

.request-type-pill {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  border: 0.1px solid black;
}

.smk-item-duty-panel {
  position: fixed;
  z-index: 9000;
  width: 430px;
  max-width: calc(100vw - 16px);
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

.smk-item-duty-table {
  border-collapse: collapse;
  width: 100%;
  font-size: 11px;
  background: #f3f3f3;
}

.smk-item-duty-table th,
.smk-item-duty-table td {
  border: 1px solid #7e8791;
  height: 20px;
  min-width: 42px;
  padding: 1px 5px;
  text-align: center;
  box-sizing: border-box;
}

.smk-item-duty-table th {
  background: #c7d5e4;
  font-weight: normal;
}

.smk-item-duty-table td:first-child {
  min-width: 78px;
  text-align: left;
  background: #d8e0e9;
}

.smk-item-duty-table td:nth-child(2),
.smk-item-duty-table td:nth-child(3),
.smk-item-duty-table td:nth-child(4) {
  background: #cbcaff;
}

.smk-item-duty-table td:nth-child(7) {
  background: #00e2df;
}

.smk-sidebar-column-toggles {
  display: flex;
  gap: 4px;
  margin-bottom: 5px;
}

.smk-column-toggle-button {
  width: 32px;
  height: 30px;
  padding: 2px;
  border: 1px solid #b8b8b8;
  border-radius: 4px;
  background: white;
  cursor: pointer;

  img {
    width: 22px;
    height: 22px;
    object-fit: contain;
    filter: grayscale(1);
    opacity: 0.65;
  }

  &.active {
    border-color: #4caf50;
    background: #edf9ee;

    img {
      filter: none;
      opacity: 1;
    }
  }
}

.smk-item-duty-panel.with-cess {
  width: 740px;
  overflow-x: auto;
}

.smk-item-hover-tables {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.smk-item-duty-table {
  flex: 0 0 430px;
}

.smk-item-cess-table {
  flex: 0 0 270px;
  width: 270px;
  border-collapse: collapse;
  font-size: 11px;
  background: #f3f3f3;
}

.smk-item-cess-table th,
.smk-item-cess-table td {
  height: 20px;
  padding: 1px 4px;
  border: 1px solid #7e8791;
  text-align: center;
  box-sizing: border-box;
}

.smk-item-cess-table th {
  background: #c7d5e4;
  font-weight: normal;
  white-space: nowrap;
}

.smk-item-cess-table td:first-child {
  min-width: 82px;
  text-align: left;
  background: #d8e0e9;
}

.smk-item-cess-table td:nth-child(2),
.smk-item-cess-table td:nth-child(3) {
  background: #cbcaff;
}

.smk-item-cess-table td:nth-child(4) {
  background: white;
}

.smk-item-cess-table td:nth-child(5) {
  background: #00e2df;
}

.smk-items-grouped-table {
  border-collapse: separate;
  border-spacing: 0;

  .smk-group-item {
    --smk-group-color: #087f73;
  }

  .smk-group-tariff {
    --smk-group-color: #b66a00;
  }

  .smk-group-invoice-qty {
    --smk-group-color: #2878b5;
  }

  .smk-group-tariff-qty {
    --smk-group-color: #7655a6;
  }

  .smk-group-price {
    --smk-group-color: #b44c67;
  }

  .smk-group-duty-qty {
    --smk-group-color: #4d7c45;
  }

  .smk-group-alcohol {
    --smk-group-color: #566573;
  }

  .smk-group-vehicle {
    --smk-group-color: #f18030;
  }

  .smk-group-warehouse {
    --smk-group-color: #16718c;
  }

  th.smk-field-group {
    background-color: var(--smk-group-color) !important;
    border-top: 2px solid var(--smk-group-color);
    border-bottom: 3px solid var(--smk-group-color);
  }

  th.smk-group-start,
  td.smk-group-start {
    box-shadow: inset 2px 0 0 var(--smk-group-color);
  }

  th.smk-group-end,
  td.smk-group-end {
    border-right: 2px solid var(--smk-group-color) !important;
  }

  th.smk-group-start {
    border-radius: 4px 0 0 0;
  }

  th.smk-group-end {
    border-radius: 0 4px 0 0;
  }
}
</style>
